import { getPermissions } from "../../utilis/permissionStorage";
import React, { useMemo, useState } from "react";
import * as FaIcons from "react-icons/fa";
import Button from "../base/Button";
import { SwalAboveAppModal } from "../../utilis/swalAboveAppModal";
import { apiUrl } from "../ApiManager/AxiosHeaders";
import { personalDocumentTypes } from "../../utilis/constants";
import { extractPersonalDocumentFromFile } from "../../utilis/personalDocExtract";
import { buildPersonalDocFormData } from "../../utilis/personalDocUpload";

const createFileEntry = (file) => ({
  file,
  document_name: "",
  description: "",
  personalCategory: "",
  isFinancial: false,
});

const getPersonalTypeTitle = (value) =>
  personalDocumentTypes.find((item) => item.value === value)?.title ||
  "Personal Document";

const getFileUploadCategory = (item) => {
  if (item.personalCategory) {
    return {
      key: "personal",
      label: getPersonalTypeTitle(item.personalCategory),
      description:
        "Personal document — information will be extracted and saved in Personal Documents tab.",
      badgeClass: "bg-indigo-100 text-indigo-800 border-indigo-200",
    };
  }
  if (item.isFinancial) {
    return {
      key: "financial",
      label: "Financial Document",
      description:
        "Financial document — will appear under the Financial Documents tab.",
      badgeClass: "bg-amber-100 text-amber-800 border-amber-200",
    };
  }
  return {
    key: "general",
    label: "General Document",
    badgeClass: "bg-gray-100 text-gray-700 border-gray-200",
  };
};

const UPLOAD_STEPS = {
  PREPARING: "preparing",
  READING: "reading",
  EXTRACTING: "extracting",
  CLEANING: "cleaning",
  UPLOADING: "uploading",
  FINISHING: "finishing",
};

function ClientDocumentUploadPanel({
  clientId,
  onSuccess,
  variant = "page",
  showTitle = true,
  inputIdPrefix = "clientDoc",
}) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const allPermissions = useMemo(() => getPermissions(), []);

  const hasFinancePermission =
    allPermissions?.finance_notes === "1" ||
    allPermissions?.finance_notes === 1;

  const user = JSON.parse(localStorage.getItem("user") || "null");
  const fileInputId = `${inputIdPrefix}FileInput-${clientId}`;
  const isModal = variant === "modal";
  const dropzonePadding = isModal ? "p-6 sm:p-8" : "p-16";

  const appendFiles = (selectedFiles) => {
    setFiles((prev) => {
      const newFiles = selectedFiles.filter(
        (newFile) =>
          !prev.some(
            (existing) =>
              existing.file.name === newFile.name &&
              existing.file.size === newFile.size,
          ),
      );
      return [...prev, ...newFiles.map((file) => createFileEntry(file))];
    });
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files).filter(
      (file) =>
        file.type === "application/pdf" || file.type.startsWith("image/"),
    );
    if (selectedFiles.length > 0) {
      appendFiles(selectedFiles);
    }
    e.target.value = "";
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleFinancialCheck = (index, checked) => {
    const updated = [...files];
    updated[index] = {
      ...updated[index],
      isFinancial: checked,
    };
    setFiles(updated);
  };

  const isBusy = isSubmitting || uploading;

  const updateProgress = (patch) => {
    setUploadProgress((prev) => ({ ...prev, ...patch }));
  };

  const uploadGeneralFiles = async (generalFiles) => {
    if (generalFiles.length === 0) return;

    const formData = new FormData();
    formData.append("clientid", clientId);
    formData.append("addedby", "cms");
    formData.append("addedid", user?.id);

    generalFiles.forEach((item) => {
      formData.append("files[]", item.file);
      formData.append("types[]", "general");
      formData.append("names[]", item.file.name);
      formData.append("document_names[]", item.document_name.trim() || "");
      formData.append("descriptions[]", item.description.trim() || "");
      formData.append("is_financial[]", item.isFinancial ? "1" : "0");
    });

    const response = await apiUrl.post(
      "upload-client-document-inProfile",
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );

    if (!response?.data?.status) {
      throw new Error(response?.data?.message || "Upload failed");
    }
  };

  const uploadPersonalFiles = async (personalFiles) => {
    const ocrMap = new Map();
    const total = personalFiles.length;

    for (let index = 0; index < personalFiles.length; index += 1) {
      const item = personalFiles[index];
      const typeTitle = getPersonalTypeTitle(item.personalCategory);
      const fileLabel = item.document_name.trim() || item.file.name;

      updateProgress({
        active: true,
        step: UPLOAD_STEPS.READING,
        fileIndex: index + 1,
        fileTotal: total,
        fileName: item.file.name,
        typeTitle,
        ocrPercent: 0,
        lines: [
          `Reading ${typeTitle} (${fileLabel})...`,
          "Scanning document pages with OCR...",
        ],
        completedLines: 0,
      });

      try {
        updateProgress({
          step: UPLOAD_STEPS.EXTRACTING,
          lines: [
            `Reading ${typeTitle} (${fileLabel})...`,
            "Extracting information from document...",
            "Detecting dates, names, and document fields...",
          ],
          completedLines: 1,
        });

        const ocr = await extractPersonalDocumentFromFile(
          item.file,
          item.personalCategory,
          (percent) => {
            updateProgress({
              step: UPLOAD_STEPS.EXTRACTING,
              ocrPercent: percent,
              lines: [
                `Reading ${typeTitle} (${fileLabel})...`,
                `Extracting information... ${percent}%`,
                "Detecting dates, names, and document fields...",
              ],
              completedLines: 1,
            });
          },
        );

        ocrMap.set(item.file, ocr || {});

        updateProgress({
          step: UPLOAD_STEPS.CLEANING,
          ocrPercent: 100,
          lines: [
            `Reading ${typeTitle} (${fileLabel})...`,
            "Information extracted successfully.",
            "Cleaning text and building JSON payload...",
          ],
          completedLines: 2,
        });
      } catch (error) {
        console.error(`[Personal OCR:${item.personalCategory}] Failed:`, error);
        ocrMap.set(item.file, {});
        updateProgress({
          lines: [
            `Reading ${typeTitle} (${fileLabel})...`,
            "Could not extract all fields — uploading file anyway.",
          ],
          completedLines: 2,
        });
      }
    }

    for (let index = 0; index < personalFiles.length; index += 1) {
      const item = personalFiles[index];
      const typeTitle = getPersonalTypeTitle(item.personalCategory);
      const fileLabel = item.document_name.trim() || item.file.name;

      updateProgress({
        step: UPLOAD_STEPS.UPLOADING,
        fileIndex: index + 1,
        fileTotal: personalFiles.length,
        lines: [
          "Document information prepared.",
          `Uploading ${typeTitle} (${fileLabel}) to server...`,
        ],
        completedLines: 2,
      });

      const ocr = ocrMap.get(item.file) || {};
      const response = await apiUrl.post(
        "upload-client-personal-document",
        buildPersonalDocFormData({
          clientId,
          userId: user?.id,
          item,
          ocr,
        }),
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      if (!response?.data?.status) {
        throw new Error(
          response?.data?.message || "Personal document upload failed.",
        );
      }
    }
  };

  const uploadFiles = async () => {
    if (isSubmitting) return;

    const personalFiles = files.filter((item) => item.personalCategory);
    const generalFiles = files.filter((item) => !item.personalCategory);

    const missingDocumentName = files.find(
      (item) => !item.document_name.trim(),
    );
    if (missingDocumentName) {
      SwalAboveAppModal.fire({
        icon: "warning",
        title: "Warning",
        text:
          "Document Name is required for each file. Please enter a name before uploading.",
      });
      return;
    }

    setIsSubmitting(true);
    setUploading(true);
    setUploadProgress({
      active: true,
      step: UPLOAD_STEPS.PREPARING,
      fileIndex: 0,
      fileTotal: files.length,
      lines: ["Preparing your documents for upload..."],
      completedLines: 0,
      ocrPercent: 0,
    });

    try {
      if (generalFiles.length > 0) {
        updateProgress({
          step: UPLOAD_STEPS.UPLOADING,
          lines: [
            `Uploading ${generalFiles.length} document(s) to server...`,
            "Please wait, do not close this page.",
          ],
          completedLines: 1,
        });
        await uploadGeneralFiles(generalFiles);
      }
      if (personalFiles.length > 0) {
        await uploadPersonalFiles(personalFiles);
      }

      updateProgress({
        step: UPLOAD_STEPS.FINISHING,
        lines: ["Upload complete.", "Refreshing document list..."],
        completedLines: 3,
        ocrPercent: 100,
      });

      SwalAboveAppModal.fire({
        icon: "success",
        title: "Success",
        text: "Uploaded successfully.",
      });
      setFiles([]);
      if (onSuccess) {
        await Promise.resolve(onSuccess());
      }
    } catch (error) {
      console.error("Upload failed:", error);
      SwalAboveAppModal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || error.message || "Upload failed.",
      });
      if (onSuccess) {
        await Promise.resolve(onSuccess());
      }
    } finally {
      setUploading(false);
      setIsSubmitting(false);
      setUploadProgress(null);
    }
  };

  return (
    <div
      className={
        isModal
          ? "flex min-h-0 flex-1 flex-col overflow-y-auto bg-slate-50/30 p-3 sm:p-4"
          : ""
      }
    >
      {showTitle && variant === "page" ? (
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100 text-lg font-bold text-blue-600 shadow-sm">
            <FaIcons.FaPlus />
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-blue-600">
            Upload Documents
          </h1>
        </div>
      ) : null}

      <div
        className={`flex flex-col items-center justify-center rounded-xl border-2 border-dashed bg-white text-center shadow-sm transition-all ${dropzonePadding} ${
          isBusy ? "pointer-events-none opacity-60" : "cursor-pointer"
        } ${
          isDragging
            ? "border-[#007BFF] bg-[#F7FAFF]"
            : isModal
              ? "border-slate-200 hover:border-[#007BFF]/50 hover:bg-[#F7FAFF]/50"
              : "border-gray-200 hover:border-blue-400"
        }`}
        onClick={() => {
          if (!isBusy) {
            document.getElementById(fileInputId)?.click();
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onDragEnter={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDragging(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDragging(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDragging(false);

          const droppedFiles = Array.from(e.dataTransfer.files).filter(
            (file) =>
              file.type === "application/pdf" || file.type.startsWith("image/"),
          );

          if (droppedFiles.length > 0) {
            appendFiles(droppedFiles);
          } else {
            SwalAboveAppModal.fire({
              icon: "warning",
              title: "Warning",
              text: "Only PDF and image files are allowed.",
            });
          }
        }}
      >
        <input
          id={fileInputId}
          type="file"
          multiple
          accept="image/*,application/pdf"
          className="hidden"
          disabled={isBusy}
          onChange={handleFileSelect}
        />
        <div
          className={`mb-4 flex items-center justify-center rounded-full bg-blue-50 ${
            isModal ? "h-12 w-12" : "h-16 w-16"
          }`}
        >
          <FaIcons.FaUpload
            className={`text-blue-500 ${isModal ? "text-2xl" : "text-3xl"}`}
          />
        </div>
        <p
          className={`font-semibold text-slate-700 ${isModal ? "text-sm" : "text-lg"}`}
        >
          {isModal ? "Drop files here or browse" : "Drag & drop your documents here"}
        </p>
        <p className="mt-1 text-xs text-slate-500">
          PDF or images only
          {!isModal ? (
            <>
              {" "}
              · or <span className="font-semibold text-blue-600">browse</span>{" "}
              from your computer
            </>
          ) : null}
        </p>
      </div>

      {files.length > 0 && (
        <div
          className={`mt-4 space-y-4 ${isBusy ? "pointer-events-none opacity-70" : ""}`}
        >
          {files.map((item, index) => {
            const category = getFileUploadCategory(item);
            return (
              <div
                key={index}
                className={`rounded-lg border border-slate-200 bg-white shadow-sm ${
                  isModal ? "p-3" : "bg-gray-50 p-4"
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <FaIcons.FaFileAlt className="mt-1 flex-shrink-0 text-xl text-blue-500" />
                    <div>
                      <p className="font-medium text-gray-700">
                        {item.file.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {Math.round(item.file.size / 1024)} KB
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFile(index)}
                    className="mt-1 p-1 text-red-500 hover:text-red-700"
                  >
                    <FaIcons.FaTrash />
                  </button>
                </div>

                {category.key !== "general" && (
                  <div
                    className={`mt-3 rounded-md border px-3 py-2 text-xs ${category.badgeClass}`}
                  >
                    <span className="font-semibold">
                      Upload as: {category.label}
                    </span>
                    <p className="mt-1 opacity-90">{category.description}</p>
                  </div>
                )}

                {hasFinancePermission && (
                  <div
                    className={`mt-3 rounded-lg border border-slate-200 bg-slate-50/80 ${
                      isModal ? "p-2.5" : "bg-white p-3"
                    }`}
                  >
                    <label className="flex cursor-pointer items-start gap-2">
                      <input
                        type="checkbox"
                        checked={item.isFinancial || false}
                        onChange={(e) =>
                          handleFinancialCheck(index, e.target.checked)
                        }
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-xs leading-snug text-slate-700">
                        <span className="font-semibold text-slate-800">
                          Financial document
                        </span>
                        <span className="mt-0.5 block text-[11px] font-normal text-slate-500">
                          Invoices, receipts, or finance-related files
                        </span>
                      </span>
                    </label>
                  </div>
                )}

                <div className="mt-3">
                  <label className="mb-1 block text-sm text-gray-600">
                    Document Name <span className="text-red-500">*</span>
                  </label>
                  <p className="mb-2 text-xs text-gray-500">
                    Required for every file.
                    {category.key === "financial"
                      ? " Financial Documents"
                      : " General Documents"}{" "}
                    tab.
                  </p>
                  <input
                    type="text"
                    value={item.document_name}
                    onChange={(e) => {
                      const updated = [...files];
                      updated[index] = {
                        ...item,
                        document_name: e.target.value,
                      };
                      setFiles(updated);
                    }}
                    className="w-full rounded border border-gray-300 p-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                    placeholder={
                      item.isFinancial
                        ? "e.g., Bank Statement, Invoice"
                        : "e.g., Contract, Agreement, Letter"
                    }
                  />
                </div>

                <div className="mt-3">
                  <label className="mb-1 block text-sm text-gray-600">
                    Description
                  </label>
                  <p className="mb-2 text-xs text-gray-500">
                    Optional notes about this document.
                  </p>
                  <textarea
                    value={item.description}
                    onChange={(e) => {
                      const updated = [...files];
                      updated[index] = { ...item, description: e.target.value };
                      setFiles(updated);
                    }}
                    rows="2"
                    className="w-full rounded border border-gray-300 p-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                    placeholder="Optional notes"
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className={isModal ? "mt-3" : "mt-4"}>
        {files.length > 0 && (
          isModal ? (
            <button
              type="button"
              disabled={isBusy}
              onClick={uploadFiles}
              className="flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-[#007BFF] text-[12px] font-semibold text-white shadow-sm transition hover:bg-[#0069D9] disabled:cursor-not-allowed disabled:opacity-60"
              style={{ border: "none" }}
            >
              {isBusy
                ? uploadProgress?.step === UPLOAD_STEPS.EXTRACTING
                  ? `Extracting… ${uploadProgress?.ocrPercent || 0}%`
                  : "Uploading…"
                : "Upload documents"}
            </button>
          ) : (
            <Button
              disable={isBusy}
              loading={isBusy}
              loadingText={
                uploadProgress?.step === UPLOAD_STEPS.EXTRACTING
                  ? `Extracting... ${uploadProgress?.ocrPercent || 0}%`
                  : uploadProgress?.step === UPLOAD_STEPS.CLEANING
                    ? "Cleaning data..."
                    : uploadProgress?.step === UPLOAD_STEPS.UPLOADING
                      ? "Uploading..."
                      : "Processing..."
              }
              text="Upload Documents"
              variant="primary"
              size="lg"
              onClick={uploadFiles}
            />
          )
        )}

        {uploadProgress?.active && (
          <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-4">
            <div className="mb-3 flex items-center gap-3">
              <FaIcons.FaSpinner className="flex-shrink-0 animate-spin text-lg text-blue-600" />
              <div>
                <p className="text-sm font-semibold text-blue-800">
                  {uploadProgress.step === UPLOAD_STEPS.PREPARING &&
                    "Preparing upload..."}
                  {uploadProgress.step === UPLOAD_STEPS.READING &&
                    "Reading document..."}
                  {uploadProgress.step === UPLOAD_STEPS.EXTRACTING &&
                    "Extracting document information..."}
                  {uploadProgress.step === UPLOAD_STEPS.CLEANING &&
                    "Cleaning and structuring data..."}
                  {uploadProgress.step === UPLOAD_STEPS.UPLOADING &&
                    "Uploading to server..."}
                  {uploadProgress.step === UPLOAD_STEPS.FINISHING &&
                    "Finishing up..."}
                </p>
                {uploadProgress.fileTotal > 0 &&
                  uploadProgress.step !== UPLOAD_STEPS.PREPARING && (
                    <p className="mt-0.5 text-xs text-blue-600">
                      File {uploadProgress.fileIndex || 1} of{" "}
                      {uploadProgress.fileTotal}
                      {uploadProgress.typeTitle
                        ? ` · ${uploadProgress.typeTitle}`
                        : ""}
                    </p>
                  )}
              </div>
            </div>

            {(uploadProgress.step === UPLOAD_STEPS.EXTRACTING ||
              uploadProgress.step === UPLOAD_STEPS.CLEANING) && (
              <div className="mb-3">
                <div className="h-2 w-full overflow-hidden rounded-full bg-blue-100">
                  <div
                    className="h-full rounded-full bg-blue-600 transition-all duration-300"
                    style={{
                      width: `${uploadProgress.ocrPercent || 5}%`,
                    }}
                  />
                </div>
              </div>
            )}

            <ul className="space-y-2">
              {(uploadProgress.lines || []).map((line, lineIndex) => {
                const isDone = lineIndex < (uploadProgress.completedLines || 0);
                const isCurrent = lineIndex === uploadProgress.completedLines;
                return (
                  <li
                    key={`${line}-${lineIndex}`}
                    className={`flex items-start gap-2 text-sm ${
                      isDone
                        ? "text-green-700"
                        : isCurrent
                          ? "font-medium text-blue-800"
                          : "text-gray-500"
                    }`}
                  >
                    {isDone ? (
                      <FaIcons.FaCheckCircle className="mt-0.5 flex-shrink-0 text-green-600" />
                    ) : isCurrent ? (
                      <FaIcons.FaSpinner className="mt-0.5 flex-shrink-0 animate-spin text-blue-600" />
                    ) : (
                      <FaIcons.FaCircle className="mt-0.5 flex-shrink-0 text-xs text-gray-300" />
                    )}
                    <span>{line}</span>
                  </li>
                );
              })}
            </ul>

            {!isModal ? (
              <p className="mt-3 text-xs text-gray-500">
                OCR and data extraction can take a minute for PDFs and multiple
                pages. Please wait until upload completes.
              </p>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

export default ClientDocumentUploadPanel;
